import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Alert, Button } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import {
  getCreditPurchaseById,
  getCreditPurchaseUsage,
  creditPurchasesPath,
} from '../../services/CreditService';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CommonTable from '../components/CommonTable/CommonTable';
import {
  formatCreditMoney,
  formatCreditDate,
  formatCreditDateTime,
  formatCreditSource,
  formatCreditStatus,
} from './creditFormatters';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './CreditWalletPage.css';

const PAGE_SIZE = 20;

const USAGE_COLUMNS = [
  {
    key: 'used_at',
    header: 'Date',
    accessor: (row) => formatCreditDateTime(row.used_at),
  },
  {
    key: 'module',
    header: 'Module',
    accessor: (row) => row.module_name || '—',
  },
  {
    key: 'operation',
    header: 'Operation',
    accessor: (row) => row.operation_key?.replace(/_/g, ' ') || '—',
    cellClassName: 'text-capitalize',
  },
  {
    key: 'quantity',
    header: 'Quantity',
    accessor: (row) => Number(row.quantity),
  },
  {
    key: 'amount',
    header: 'From this batch',
    accessor: (row) => `$${formatCreditMoney(row.amount_from_batch_usd)}`,
  },
  {
    key: 'total',
    header: 'Total deducted',
    accessor: (row) => `$${formatCreditMoney(row.amount_deducted_usd)}`,
  },
  {
    key: 'user',
    header: 'User',
    accessor: (row) => row.used_by || '—',
  },
];

function DetailItem({ label, value }) {
  return (
    <div className="credit-detail-item">
      <span className="credit-detail-label">{label}</span>
      <span className="credit-detail-value">{value}</span>
    </div>
  );
}

export default function CreditPurchaseDetailPage() {
  const navigate = useNavigate();
  const { purchaseId } = useParams();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [usages, setUsages] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const [purchaseRes, usageRes] = await Promise.all([
        getCreditPurchaseById(purchaseId),
        getCreditPurchaseUsage(purchaseId, { limit: PAGE_SIZE, offset }),
      ]);

      if (!purchaseRes?.success) {
        setError(purchaseRes?.message || 'Failed to load credit details');
        return;
      }

      setPurchase(purchaseRes.purchase);

      if (usageRes?.success) {
        setUsages(usageRes.usages || []);
        setTotalPages(usageRes.totalPages || 1);
        setTotal(usageRes.total || 0);
      } else {
        setUsages([]);
        setTotalPages(1);
        setTotal(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load credit details');
    } finally {
      setLoading(false);
    }
  }, [purchaseId, page]);

  useEffect(() => {
    setPage(1);
  }, [purchaseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(creditPurchasesPath());
    }
  };

  return (
    <div className="credit-wallet-page">
      <button type="button" className="credit-wallet-back-button" onClick={handleBack}>
        <FiArrowLeft style={{ marginRight: '0.5rem' }} aria-hidden="true" />
        Back
      </button>

      <Row className="credit-wallet-row">
        <Col lg={12}>
          <div className="user-list-card credit-wallet-main-card">
            <div className="credit-wallet-header">
              <div className="credit-wallet-header-text">
                <h5 className="credit-wallet-title">Credit batch #{purchaseId}</h5>
                <p className="credit-wallet-subtitle mb-0">
                  Credit batch details and usage drawn from this purchase.
                </p>
              </div>
            </div>

            <div className="credit-wallet-content">
              {loading && (
                <div className="loading-container credit-wallet-loading">
                  <div className="loading-spinner" />
                </div>
              )}

              {!loading && error && (
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              )}

              {!loading && !error && purchase && (
                <>
                  <section className="credit-wallet-block">
                    <h6 className="credit-wallet-section-title">Batch details</h6>
                    <div className="credit-batch-detail-card">
                      <div className="credit-detail-grid">
                        <DetailItem label="Reference" value={`#${purchase.ref_id}`} />
                        <DetailItem
                          label="Source"
                          value={formatCreditSource(purchase.source)}
                        />
                        <DetailItem label="Status" value={formatCreditStatus(purchase.status)} />
                        <DetailItem
                          label="Purchased"
                          value={`$${formatCreditMoney(purchase.amount_purchased_usd)}`}
                        />
                        <DetailItem
                          label="Used"
                          value={`$${formatCreditMoney(purchase.amount_used_usd)}`}
                        />
                        <DetailItem
                          label="Remaining balance"
                          value={`$${formatCreditMoney(purchase.balance)}`}
                        />
                        <DetailItem
                          label="Purchase date"
                          value={formatCreditDate(purchase.purchase_date)}
                        />
                        <DetailItem
                          label="Valid until"
                          value={formatCreditDate(purchase.validity_date)}
                        />
                        {purchase.module_name && (
                          <DetailItem label="Module" value={purchase.module_name} />
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="credit-wallet-block">
                    <h6 className="credit-wallet-section-title">Usage from this batch</h6>
                    <div className="row g-0 credit-wallet-table-row">
                      <CommonTable
                        columns={USAGE_COLUMNS}
                        data={usages}
                        emptyMessage="No usage recorded for this batch yet"
                        emptyStateIcon="fas fa-chart-line"
                        getRowId={(row) => row.id}
                        minHeight="0"
                        maxHeight="none"
                        className="credit-wallet-common-table"
                      />
                    </div>

                    {totalPages > 1 && (
                      <div className="credit-pagination">
                        <span className="credit-pagination-meta">
                          Page {page} of {totalPages} ({total} items)
                        </span>
                        <div className="credit-pagination-actions">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            Previous
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="ms-2"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <InternalFooter />
    </div>
  );
}
